import { Component, input, InputSignal, OnDestroy, OnInit } from '@angular/core';
import { ApplyColumnStateParams, CellClickedEvent, ColDef, GridApi, GridReadyEvent, IRowNode } from 'ag-grid-community';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../../../Compliance/AgGrid';
import { AgGridToolPanelButton } from '../../../Compliance/AgGrid/ToolPanel/models';
import { AgGridExtensionsModule } from '../../../Compliance/AgGrid/agGridExtensions.module';
import { WsAgGridWrapperComponent } from '../../../Compliance/AgGrid/AgGridWrapper/wsAgGridWrapper.component';
import { AgGridRowCountComponent } from '../../../Compliance/AgGrid/Row-Count/agGridRowCount.component';
import { DirectivesModule } from '../../../UI-Lib/Directives/directives.module';
import {
    AgGridMultiSelectCellRendererParams,
    AgGridMultiSelectedCellRenderer,
    AgGridMultiSelectedHeaderRenderer,
    AgGridMultiSelectRendererParams,
    AgGridMultiSelectTracker,
} from '../../../Compliance/AgGrid/MultiSelectTracker';
import {
    AgGridLinkCellRendererComponent,
    AgGridLinkCellRendererParams,
} from '../../../Compliance/AgGrid/CellRenderers/agGridLinkCellRenderer.component';
import {
    ATTACHMENT_LOCATION_AVAILABLE_COLUMNS,
    ATTACHMENT_LOCATION_VISIBLE_COLUMNS,
    AttachmentBundlerService,
} from '../attachmentBundler.service';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { AttachmentService } from '../../attachment.service';
import { DocumentService } from '../../../Documents/document.service';
import { PopUpService } from '../../../Common/Popup/popup.service';
import { UpgradeNavigationServiceHandler } from '../../../Common/Routing/upgrade-navigation-handler.service';

@Component({
    selector: 'bundler-organize-list',
    templateUrl: './bundlerOrganizeList.component.html',
    styleUrls: ['./bundlerOrganizeList.component.scss'],
    imports: [WsAgGridWrapperComponent, AgGridRowCountComponent, DirectivesModule, AgGridExtensionsModule],
})
export class BundlerOrganizeListComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _attachmentBundlerService: AttachmentBundlerService,
        private readonly _attachmentService: AttachmentService,
        private readonly _documentService: DocumentService,
        private readonly _popUpService: PopUpService,
        private readonly _upgradeNavigationServiceHandler: UpgradeNavigationServiceHandler,
    ) {}

    bundlerTarget: InputSignal<Core.AttachmentBundlerScopeEnum> = input();

    documentGridId: string = 'a0b33e04-f778-4c0e-9435-539a6a83366d';
    selectionGridId: string = 'a0b33e04-f778-4c0e-9435-539a6a83366d';

    documentGridTracker: AgGridMultiSelectTracker;
    selectionGridTracker: AgGridMultiSelectTracker;

    documentGridOptions = new AgGridOptionsBuilder({
        rowDragManaged: true,
        rowDragMultiRow: true,
        suppressMoveWhenRowDragging: true,
        rowClassRules: {
            'ag-row-selected': (params) =>
                params.data && this.documentGridTracker.isRowSelected(params.data.attachmentId),
        },
    })
        .withMultipleRowSelect()
        .withColumnResize()
        .withSort()
        .withLoadingOverlay()
        .withContext(this)
        .build();
    selectionGridOptions = new AgGridOptionsBuilder({
        rowDragManaged: true,
        suppressRowDrag: true,
        rowClassRules: {
            'ag-row-selected': (params) =>
                params.data && this.selectionGridTracker.isRowSelected(params.data.attachmentId),
        },
    })
        .withColumnResize()
        .withSort()
        .withLoadingOverlay()
        .withContext(this)
        .build();

    documentGridApi: GridApi;
    selectionGridApi: GridApi;

    get additionalButtons(): AgGridToolPanelButton[] {
        return this._canShowMoveSelectedAttachmentsButton() ? this._additionalButtons : [];
    }

    isBulkDeleteVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    // Columns that are relevant to the current data but hidden by default. Users can enable
    // them from the grid's column tool panel. Corresponds to the "orange" cells in the spec.
    availableColumns: string[] = [];
    visibleColumns: string[] = [
        'location',
        'attachmentType',
        'image',
        'topLevelCompanyName',
        'companyName',
        'siteName',
        'parcelAccountNumber',
        'propertyType',
        'state',
        'assessorName',
        'collectorName',
        'year',
        'additionalDetails',
    ];

    private _attachmentListData = [];
    private _selectionListData = [];
    private _destroy$: Subject<void> = new Subject();

    private _additionalButtons: AgGridToolPanelButton[] = [
        {
            icon: 'fa-arrow-right',
            getTooltipText: () => 'Move selected attachments',
            disabled: () => !this._hasSelectedDocumentRows(),
            onClickCallback: () => this._moveSelectedAttachmentsToSelectionGrid(),
        },
    ];

    ngOnInit(): void {
        // The grids are driven entirely by the service observables. Mutations (drag/drop, bulk delete)
        // only update the service, which re-emits the filtered attachment list and the selected list;
        // each grid then refreshes through a single `rowData` assignment instead of per-row transactions,
        // which is dramatically faster for large (100k+) row counts.
        this._attachmentBundlerService.attachmentList$.pipe(takeUntil(this._destroy$)).subscribe((x) => {
            this._attachmentListData = x ?? [];
            // Recompute visible columns whenever non-empty data arrives, even if the grid is not ready yet,
            // so the initial column defs built in `onDocumentAgGridReady()` reflect the current data.
            if (this._attachmentListData.length > 0) {
                this._updateVisibleColumnsFromData(this._attachmentListData);
            }
            if (!this.documentGridApi) {
                return;
            }
            if (this._attachmentListData.length > 0) {
                this._refreshGridColumns();
            }
            this.documentGridApi.setGridOption('rowData', this._attachmentListData);
        });

        this._attachmentBundlerService.selectedAttachments$.pipe(takeUntil(this._destroy$)).subscribe((x) => {
            this._selectionListData = x ?? [];
            if (this.selectionGridApi) {
                this.selectionGridApi.setGridOption('rowData', this._selectionListData);
            }
        });

        this._attachmentBundlerService.loading$.pipe(takeUntil(this._destroy$)).subscribe((loading) => {
            // Reset selection tracker only when a new load starts (e.g. filter change).
            if (loading) {
                this.documentGridTracker?.clear();
            }
            if (this.documentGridApi) {
                this.documentGridApi.setGridOption('loading', loading);
            }
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    onDocumentAgGridReady(event: GridReadyEvent): void {
        this.documentGridApi = event.api;

        this.documentGridTracker = new AgGridMultiSelectTracker(
            this.documentGridOptions,
            this.documentGridApi,
            this._getDocumentGridRowIds.bind(this),
        );

        // Ensure visible columns reflect any attachment data that arrived before the grid was ready,
        // so the initial column defs are derived from the current data instead of the stale defaults.
        if (this._attachmentListData.length > 0) {
            this._updateVisibleColumnsFromData(this._attachmentListData);
        }

        const columnDefs: ColDef[] = [
            {
                field: 'attachmentId',
                width: AgGridColumns.selectionColumnWidth,
                maxWidth: AgGridColumns.selectionColumnWidth,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressMovable: true,
                suppressColumnsToolPanel: true,
                pinned: 'left',
                lockPinned: true,
                headerComponent: AgGridMultiSelectedHeaderRenderer,
                headerComponentParams: { tracker: this.documentGridTracker } as AgGridMultiSelectRendererParams,
                cellRenderer: AgGridMultiSelectedCellRenderer,
                cellRendererParams: {
                    tracker: this.documentGridTracker,
                    isCellRendererDisabledFn: (_value: number, data: Core.AttachmentBundlerItemModel) =>
                        this._attachmentBundlerService.isAlreadyAttached(data?.attachmentId?.toString() ?? ''),
                } as AgGridMultiSelectCellRendererParams,
            },
            ...this._getColumnDefs(),
        ];

        this._setDropZone();

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'location', sort: 'asc' }],
            defaultState: { sort: null },
        };

        this.documentGridApi.setGridOption('columnDefs', columnDefs);
        this.documentGridApi.setGridOption('rowData', this._attachmentListData);
        this.documentGridApi.applyColumnState(defaultSortModel);
    }

    onSelectionAgGridReady(event: GridReadyEvent): void {
        this.selectionGridApi = event.api;

        this.selectionGridTracker = new AgGridMultiSelectTracker(
            this.selectionGridOptions,
            this.selectionGridApi,
            this._getSelectionGridRowIds.bind(this),
        );

        this.selectionGridTracker.selectedRows$.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this.isBulkDeleteVisible$.next(this.selectionGridTracker.hasSelectedRows());
        });

        this._setDropZone();

        const columnDefs: ColDef[] = [
            {
                field: 'attachmentId',
                width: AgGridColumns.selectionColumnWidth,
                maxWidth: AgGridColumns.selectionColumnWidth,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressMovable: true,
                suppressColumnsToolPanel: true,
                pinned: 'left',
                lockPinned: true,
                headerComponent: AgGridMultiSelectedHeaderRenderer,
                headerComponentParams: { tracker: this.selectionGridTracker } as AgGridMultiSelectRendererParams,
                cellRenderer: AgGridMultiSelectedCellRenderer,
                cellRendererParams: {
                    tracker: this.selectionGridTracker,
                } as AgGridMultiSelectCellRendererParams,
            },
            ...this._getColumnDefs(false),
        ];

        this.selectionGridApi.setGridOption('columnDefs', columnDefs);
        this.selectionGridApi.setGridOption('rowData', this._selectionListData);
    }

    bulkDelete(): void {
        this.documentGridApi.setGridOption('loading', true);
        this.selectionGridApi.setGridOption('loading', true);

        // Collect the rows to keep (everything not selected for deletion) from the stored dataset.
        // Updating the service with this list re-emits both the selected list and the filtered
        // attachment list, so the deleted rows return to the document grid automatically via the
        // rowData subscriptions.
        const keepData = this._selectionListData.filter(
            (data) => !this.selectionGridTracker.isRowSelected(data.attachmentId),
        );

        this.documentGridTracker.clear();
        this.selectionGridTracker.clear();
        this._attachmentBundlerService.setSelectedAttachments(keepData);

        this.documentGridApi.setGridOption('loading', false);
        this.selectionGridApi.setGridOption('loading', false);
    }

    private _getColumnDefs(includeRowDrag: boolean = true): ColDef[] {
        const allColumns: ColDef[] = [
            {
                headerName: 'Attachment Location',
                field: 'location',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Attachment Type',
                field: 'attachmentType',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Image',
                field: 'image',
                width: AgGridColumns.checkboxColumnMinWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.booleanFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.booleanFloatingFilterParams,
                cellRenderer: (params) => {
                    if (params.data?.attachmentId) {
                        if (params.colDef.pdfLoading && params.colDef.pdfLoading === params.value) {
                            return `<div class="text-center" style="margin-top: 0;">
                                            <i class="fa fa-spinner fa-pulse fa-1x"></i>
                                        </div>`;
                        } else {
                            return '<span class="anchor-style"><i class="fa fa-file-text-o" aria-hidden="true"></i></span>';
                        }
                    }
                    return '';
                },
                onCellClicked: async (params: CellClickedEvent) => {
                    // Anything using square bracket notation to access or assign a value is not supported by the
                    // Typing assigned to it, although it was part of the old JS code so I'm leaving it in place

                    const rowData = params.data;

                    params.colDef['pdfLoading'] = params.value;
                    params.api.redrawRows();

                    const attachmentId = rowData.attachmentId;

                    const response = await this._documentService.getDocumentBody(attachmentId);
                    const fileName = this._attachmentService.getFileNameFromHttpResponse(response);
                    const fileExtension = fileName.split('.')[1].substring(0, 3);
                    const attachmentBlob = new Blob([response.body], { type: fileExtension });
                    const fileInfo = this._attachmentService.getMimeTypeByFileExtension(fileExtension);

                    if (fileInfo['previewSupported']) {
                        const popUpData = {
                            attachmentInfo: {
                                attachmentID: attachmentId,
                                fileName: fileName,
                                fileExtension: fileExtension,
                            },
                        };

                        let attchmentGroupName = this._popUpService.StaticAliases.Attachment;

                        // ctrl-click - open a new window
                        if (params.event['ctrlKey']) {
                            attchmentGroupName = attchmentGroupName + new Date().getTime();
                        }

                        const globalAttachmentsPopUpRef = this._popUpService.getByAliasGroup(attchmentGroupName)[0];
                        if (globalAttachmentsPopUpRef && globalAttachmentsPopUpRef.isOpen()) {
                            globalAttachmentsPopUpRef.publish(
                                this._popUpService.Topics.Attachment.AttachmentChanged,
                                popUpData,
                            );
                        } else {
                            const popUpRef = await this._popUpService.openAttachmentPreview(
                                attchmentGroupName,
                                globalAttachmentsPopUpRef,
                            );
                            popUpRef.publish(this._popUpService.Topics.Attachment.AttachmentChanged, popUpData);
                        }
                    } else {
                        await this._documentService.saveFile(attachmentBlob, fileName);
                    }

                    params.colDef['pdfLoading'] = undefined;
                    params.api.redrawRows();
                },
            },
            {
                headerName: 'Attachment Description',
                field: 'attachmentDescription',
                width: AgGridColumns.textColumnWidth,
                hide: true,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Top Level Company Name',
                field: 'topLevelCompanyName',
                width: AgGridColumns.textColumnWidth,
                hide: !includeRowDrag || this.bundlerTarget() === Core.AttachmentBundlerScopeEnum.Jurisdiction,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.topLevelCompanyName,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.topLevelCompanyId) return '';
                        return this._upgradeNavigationServiceHandler.getHrefFromState('company', {
                            companyId: params.data.topLevelCompanyId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => !params.data?.topLevelCompanyId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Company Name',
                field: 'companyName',
                width: AgGridColumns.textColumnWidth,
                hide: !includeRowDrag || this.bundlerTarget() === Core.AttachmentBundlerScopeEnum.Jurisdiction,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.companyName,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.companyId) return '';
                        return this._upgradeNavigationServiceHandler.getHrefFromState('company', {
                            companyId: params.data.companyId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => !params.data?.companyId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Site Name',
                field: 'siteName',
                width: AgGridColumns.textColumnWidth,
                hide: !includeRowDrag || this.bundlerTarget() === Core.AttachmentBundlerScopeEnum.Jurisdiction,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.siteName,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.siteId || !params.data?.companyId) return '';
                        return this._upgradeNavigationServiceHandler.getHrefFromState('site', {
                            siteId: params.data.siteId,
                            companyId: params.data.companyId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) =>
                        !params.data?.siteId || !params.data?.companyId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Parcel Acct Num',
                field: 'parcelAccountNumber',
                width: AgGridColumns.textColumnWidth,
                hide: !includeRowDrag || this.bundlerTarget() === Core.AttachmentBundlerScopeEnum.Jurisdiction,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.parcelAccountNumber,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.parcelId || !params.data?.siteId || !params.data?.companyId) return '';
                        return this._upgradeNavigationServiceHandler.getHrefFromState('parcel', {
                            parcelId: params.data.parcelId,
                            siteId: params.data.siteId,
                            companyId: params.data.companyId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) =>
                        !params.data?.parcelId || !params.data?.siteId || !params.data?.companyId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Property Type Abbr',
                field: 'propertyType',
                width: AgGridColumns.textColumnSmallWidth,
                hide: !includeRowDrag || this.bundlerTarget() === Core.AttachmentBundlerScopeEnum.Jurisdiction,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'State',
                field: 'state',
                width: AgGridColumns.stateAbbreviationColumnWidth,
                hide: !includeRowDrag,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Assessor',
                field: 'assessorName',
                width: AgGridColumns.textColumnWidth,
                hide: !includeRowDrag,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.assessorName,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.assessorId) return '';
                        return this._upgradeNavigationServiceHandler.getHrefFromState('assessorRedirect', {
                            entityId: params.data.assessorId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => !params.data?.assessorId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Collector',
                field: 'collectorName',
                width: AgGridColumns.textColumnWidth,
                hide: !includeRowDrag,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.collectorName,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.collectorId) return '';
                        return this._upgradeNavigationServiceHandler.getHrefFromState('collectorRedirect', {
                            entityId: params.data.collectorId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => !params.data?.collectorId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Year',
                field: 'year',
                width: AgGridColumns.numericColumnWidth,
                hide: !includeRowDrag,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
            },
            {
                headerName: 'Additional Details',
                field: 'additionalDetails',
                width: AgGridColumns.textColumnWidth,
                hide: !includeRowDrag,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
        ];

        const columns = allColumns
            .filter((col) => this.visibleColumns.includes(col.field) || this.availableColumns.includes(col.field))
            .map((col) => {
                // Columns that are only "available" (not part of the default/visible set) are
                // included but hidden by default so users can enable them via the tool panel.
                const isAvailableOnly = !this.visibleColumns.includes(col.field);
                return isAvailableOnly ? { ...col, hide: true } : col;
            });

        return [
            ...(includeRowDrag
                ? [
                      {
                          rowDrag: (params) =>
                              !this._attachmentBundlerService.isAlreadyAttached(params.data?.attachmentId),
                          maxWidth: 50,
                          suppressHeaderMenuButton: true,
                          suppressHeaderFilterButton: true,
                          suppressSizeToFit: true,
                          suppressAutoSize: true,
                          resizable: false,
                          suppressMovable: true,
                          suppressColumnsToolPanel: true,
                          pinned: 'left' as const,
                          lockPinned: true,
                          rowDragText: () => {
                              // We are using our own selection tracking, so we need to use that instead of the value provided by AgGrid
                              const selectedCount = this.documentGridTracker.getSelectedRowsCount() || 1;
                              return `${selectedCount.toLocaleString('en-US')} attachment${selectedCount > 1 ? 's' : ''}`;
                          },
                      },
                  ]
                : []),
            ...columns,
        ];
    }

    private _setDropZone(): void {
        if (!this.documentGridApi || !this.selectionGridApi) {
            return;
        }

        const dropZoneParams = this.selectionGridApi.getRowDropZoneParams({
            onDragStop: (params) => {
                const draggedRows = params?.nodes?.map((node) => node?.data) ?? [];
                this._moveSelectedAttachmentsToSelectionGrid(draggedRows);
            },
        });

        if (dropZoneParams) {
            this.documentGridApi.addRowDropZone(dropZoneParams);
        }
    }

    private _updateVisibleColumnsFromData(data: Core.AttachmentBundlerItemModel[]): void {
        const locations = new Set(data.map((item) => item.location));
        const columnsToShow = new Set<string>(['location']);
        const availableToShow = new Set<string>();

        locations.forEach((location) => {
            const locationColumns = ATTACHMENT_LOCATION_VISIBLE_COLUMNS[location];
            if (locationColumns) {
                locationColumns.forEach((col) => columnsToShow.add(col));
            } else {
                this.visibleColumns.forEach((col) => columnsToShow.add(col));
            }

            const locationAvailableColumns = ATTACHMENT_LOCATION_AVAILABLE_COLUMNS[location];
            if (locationAvailableColumns) {
                locationAvailableColumns.forEach((col) => availableToShow.add(col));
            } else {
                this.availableColumns.forEach((col) => availableToShow.add(col));
            }
        });

        this.visibleColumns = Array.from(columnsToShow);
        // Only keep available columns that aren't already shown by default.
        this.availableColumns = Array.from(availableToShow).filter((col) => !columnsToShow.has(col));
    }

    private _refreshGridColumns(): void {
        if (this.documentGridApi) {
            const columnDefs: ColDef[] = [
                {
                    field: 'attachmentId',
                    width: AgGridColumns.selectionColumnWidth,
                    maxWidth: AgGridColumns.selectionColumnWidth,
                    suppressSizeToFit: true,
                    suppressAutoSize: true,
                    resizable: false,
                    suppressMovable: true,
                    suppressColumnsToolPanel: true,
                    pinned: 'left',
                    lockPinned: true,
                    headerComponent: AgGridMultiSelectedHeaderRenderer,
                    headerComponentParams: { tracker: this.documentGridTracker } as AgGridMultiSelectRendererParams,
                    cellRenderer: AgGridMultiSelectedCellRenderer,
                    cellRendererParams: {
                        tracker: this.documentGridTracker,
                    } as AgGridMultiSelectCellRendererParams,
                },
                ...this._getColumnDefs(),
            ];
            this.documentGridApi.setGridOption('columnDefs', columnDefs);
        }

        if (this.selectionGridApi) {
            const columnDefs: ColDef[] = [
                {
                    field: 'attachmentId',
                    width: AgGridColumns.selectionColumnWidth,
                    maxWidth: AgGridColumns.selectionColumnWidth,
                    suppressSizeToFit: true,
                    suppressAutoSize: true,
                    resizable: false,
                    suppressMovable: true,
                    suppressColumnsToolPanel: true,
                    pinned: 'left',
                    lockPinned: true,
                    headerComponent: AgGridMultiSelectedHeaderRenderer,
                    headerComponentParams: { tracker: this.selectionGridTracker } as AgGridMultiSelectRendererParams,
                    cellRenderer: AgGridMultiSelectedCellRenderer,
                    cellRendererParams: {
                        tracker: this.selectionGridTracker,
                    } as AgGridMultiSelectCellRendererParams,
                },
                ...this._getColumnDefs(false),
            ];
            this.selectionGridApi.setGridOption('columnDefs', columnDefs);
        }
    }

    private _getDocumentGridRowIds(skip, take): Promise<Compliance.QueryResultModel<number>> {
        return this._getRowIds(this.documentGridApi, skip, take);
    }

    private _getSelectionGridRowIds(skip, take): Promise<Compliance.QueryResultModel<number>> {
        return this._getRowIds(this.selectionGridApi, skip, take);
    }

    private _getRowIds(gridApi: GridApi, skip, take): Promise<Compliance.QueryResultModel<number>> {
        const model = { rowsToDisplay: [] };
        gridApi.forEachNodeAfterFilterAndSort((x) => model.rowsToDisplay.push(x));
        const rows: IRowNode[] = model.rowsToDisplay.slice(skip, take + 1);
        return Promise.resolve({
            data: rows.map((x) => {
                return x.data.attachmentId;
            }),
        } as Compliance.QueryResultModel<number>);
    }

    private _hasSelectedDocumentRows(): boolean {
        return !!this.documentGridTracker && this.documentGridTracker.getSelectedRowsCount() > 0;
    }

    private _canShowMoveSelectedAttachmentsButton(): boolean {
        return (
            !!this.documentGridApi &&
            !!this.selectionGridApi &&
            !!this.documentGridTracker &&
            !!this.selectionGridTracker &&
            this.documentGridTracker.hasSelectedRows()
        );
    }

    private _moveSelectedAttachmentsToSelectionGrid(draggedRows: Core.AttachmentBundlerItemModel[] = []): void {
        if (!this.documentGridApi || !this.selectionGridApi || !this.documentGridTracker || !this.selectionGridTracker) {
            return;
        }

        this.documentGridApi.setGridOption('loading', true);
        this.selectionGridApi.setGridOption('loading', true);

        // isSelectAllChecked() covers selectAllRows=true (no filter) and
        // _gridHasFiltersAndSelectAllChecked=true (filtered). In either select-all mode,
        // iterate visible nodes via isRowSelected so filtered-out and explicitly-deselected
        // rows are excluded even if physically dragged. In individual mode, seed from
        // draggedRows then expand via _attachmentListData so checked-but-filtered-out rows
        // are still included.
        const rowsToMove = new Set<Core.AttachmentBundlerItemModel>();
        if (this.documentGridTracker.isSelectAllChecked()) {
            this.documentGridApi.forEachNodeAfterFilterAndSort((node) => {
                if (node.data && this.documentGridTracker.isRowSelected(node.data.attachmentId)) {
                    rowsToMove.add(node.data);
                }
            });
        } else {
            draggedRows.forEach((data) => {
                if (data) {
                    rowsToMove.add(data);
                }
            });
            this._attachmentListData.forEach((data) => {
                if (this.documentGridTracker.isRowSelected(data.attachmentId)) {
                    rowsToMove.add(data);
                }
            });
        }

        // Filter out already-attached items.
        const rowsToAdd = Array.from(rowsToMove).filter(
            (data: Core.AttachmentBundlerItemModel) =>
                !this._attachmentBundlerService.isAlreadyAttached(data.attachmentId as string),
        );

        if (rowsToAdd.length === 0) {
            this.documentGridApi.setGridOption('loading', false);
            this.selectionGridApi.setGridOption('loading', false);
            return;
        }

        // Build the new selection list (current selection + newly added) and push it to the
        // service. Both grids refresh through their rowData subscriptions rather than per-row
        // add/remove transactions, which is far faster for very large row counts.
        const existingIds = new Set(this._selectionListData.map((row) => row.attachmentId));
        const uniqueRowsToAdd = rowsToAdd.filter((data) => !existingIds.has(data.attachmentId));
        const allRows: Core.AttachmentBundlerItemModel[] = [...this._selectionListData, ...uniqueRowsToAdd];

        this.documentGridTracker.clear();
        this.selectionGridTracker.clear();
        this._attachmentBundlerService.setSelectedAttachments(allRows);

        this.documentGridApi.setGridOption('loading', false);
        this.selectionGridApi.setGridOption('loading', false);
    }
}
